import { useCallback, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { isSameEntityId } from '../utils/entityId'

export interface UseLookupRefreshOptions<T> {
  refetch: () => Promise<unknown>
  isFetching: boolean
  items: T[]
  selectedId: unknown
  getItemId?: (item: T) => unknown
  onClearSelection?: () => void
  /** When false, only refetch — do not clear selection if missing from the current list (e.g. server-side search). */
  validateSelection?: boolean
  language?: string
}

/**
 * Shared refresh behavior for searchable dropdowns:
 * - Forces a network refetch (bypasses stale React Query cache display)
 * - Keeps the dropdown open and preserves search text (caller controls those)
 * - Clears selection + shows a message if the selected item disappeared
 */
export function useLookupRefresh<T>({
  refetch,
  isFetching,
  items,
  selectedId,
  getItemId = (item: T) => (item as { id?: unknown }).id,
  onClearSelection,
  validateSelection = true,
  language = 'ar',
}: UseLookupRefreshOptions<T>) {
  const pendingValidateRef = useRef(false)
  const getItemIdRef = useRef(getItemId)
  const onClearSelectionRef = useRef(onClearSelection)

  useEffect(() => {
    getItemIdRef.current = getItemId
  }, [getItemId])

  useEffect(() => {
    onClearSelectionRef.current = onClearSelection
  }, [onClearSelection])

  useEffect(() => {
    if (!pendingValidateRef.current || isFetching) return
    pendingValidateRef.current = false

    if (!validateSelection || !onClearSelectionRef.current) return

    const hasSelection =
      selectedId !== undefined && selectedId !== null && String(selectedId).trim() !== ''
    if (!hasSelection) return

    const stillExists = items.some((item) =>
      isSameEntityId(getItemIdRef.current(item), selectedId)
    )
    if (!stillExists) {
      onClearSelectionRef.current()
      toast(
        language === 'ar'
          ? 'تم إلغاء التحديد لأن العنصر المحدد لم يعد متاحًا'
          : 'Selection cleared because the selected item is no longer available',
        { icon: 'ℹ️' }
      )
    }
  }, [items, isFetching, selectedId, validateSelection, language])

  const onRefresh = useCallback(
    async (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      if (validateSelection && onClearSelectionRef.current) {
        pendingValidateRef.current = true
      }
      try {
        await refetch()
      } catch {
        pendingValidateRef.current = false
        toast.error(language === 'ar' ? 'فشل تحديث القائمة' : 'Failed to refresh list')
      }
    },
    [refetch, validateSelection, language]
  )

  return { onRefresh, isRefreshing: isFetching }
}
